<script>
  import { format } from 'svelte-i18n';
  import { Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';

  import { sanitizeTagValue } from '@client/utils/sanitize';

  export let data = {};
  export let isDomains = false;
  export let valid = true;
  export let campaigns = [];
  export let tags = [];
  export let onKeyUpHandler = () => {};

  let filterText = '';

  const onTagChange = (e) => {
    const currentTags = data?.tags || [];

    filterText = sanitizeTagValue(filterText);

    if (!data.tags) {
      data.tags = [];
      return;
    }

    if (e.key === 'Enter' || e.type === 'blur') {
      if (data.tags.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }

      if (!currentTags.includes(filterText) && filterText.length) {
        data.tags = [{ label: filterText, value: filterText }, ...(data.tags || [])];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail.value) {
        const selected = e.detail.value;

        if (!currentTags.includes(selected) && selected.length) {
          data.tags = [selected, ...data.tags];
        }
      }
    }
  };

  $: sanitizedFilter = sanitizeTagValue(filterText || '');
  $: tagOptions =
    sanitizedFilter.length &&
    !tags.some(({ value }) => value === sanitizedFilter) &&
    !(data.tags || []).some(({ value }) => value === sanitizedFilter)
      ? [{ label: sanitizedFilter, value: sanitizedFilter, created: true }, ...tags]
      : tags;

  $: type = isDomains ? $format('label.DOMAIN') : $format('label.RECIPIENT');
</script>

<div class="form">
  <div class="section" aria-labelledby="type-label">
    <p id="type-label">
      {isDomains ? $format('label.DOMAIN') : $format('label.RECIPIENT')}
      <Icon name="help-circle" id="tooltip-group-targeting-type" />
      <Tooltip target="tooltip-group-targeting-type" placement="top">
        {#if isDomains}
          {@html $format('label.TOOLTIP.ACCOUNT_TARGETING_DOMAIN')}
        {:else}
          {@html $format('label.TOOLTIP.ACCOUNT_TARGETING_RECIPIENT')}
        {/if}
      </Tooltip>
    </p>
    <Input
      type="text"
      placeholder={$format('label.PLACEHOLDER.INPUT', {
        values: { param: type }
      })}
      invalid={!valid}
      feedback={$format('label.VALIDATION_NAME_EXISTS', {
        values: { type: $format('label.TARGETING'), param: type }
      })}
      on:keyup={onKeyUpHandler}
      bind:value={data.name}
    />
  </div>
  <div class="section" aria-labelledby="campaigns-label">
    <p id="campaigns-label">
      {$format('label.CAMPAIGNS')}
      <Icon name="help-circle" id="tooltip-group-targeting-campaigns" />
      <Tooltip target="tooltip-group-targeting-campaigns" placement="top">
        <b>{$format('label.CAMPAIGNS')}</b>
        <div>{$format('label.TOOLTIP.ACCOUNT_TARGETING_CAMPAIGNS')}</div>
      </Tooltip>
    </p>
    <Select
      bind:items={campaigns}
      bind:value={data.campaigns}
      placeholder={$format('label.PLACEHOLDER.SELECT_CAMPAIGNS')}
      multiple
      searchable
    />
  </div>
  <div class="section" aria-labelledby="tags-label" on:keyup={onTagChange} role="button" tabindex="0">
    <p id="tags-label">
      {$format('label.TAGS')}
      <Icon name="help-circle" id="tooltip-group-targeting-tags" />
      <Tooltip target="tooltip-group-targeting-tags" placement="top">
        <b>{$format('label.TAGS')}</b>
        <div>{$format('label.TOOLTIP.ACCOUNT_TARGETING_TAGS')}</div>
      </Tooltip>
    </p>
    <Select
      items={tagOptions}
      itemFilter={(label, filterText, option) =>
        option?.created || `${label}`.toLowerCase().includes(filterText.toLowerCase())}
      bind:value={data.tags}
      bind:filterText
      on:change={onTagChange}
      on:blur={onTagChange}
      placeholder={$format('label.PLACEHOLDER.SELECT_TAGS')}
      multiple
      searchable
    >
      <svelte:fragment slot="item" let:item>
        {#if item.created}
          {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: item.label } })}
        {:else}
          {item.label}
        {/if}
      </svelte:fragment>
      <slot name="empty" slot="empty">
        <div class="empty">
          {#if !filterText.length}
            {$format('label.START_TYPING_TO_FIND_TAGS')}
          {:else if data.tags?.map(({ value }) => value).includes(filterText)}
            {$format('label.TAG_ALREADY_EXISTS')}
          {:else}
            {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: filterText } })}
          {/if}
        </div>
      </slot>
    </Select>
  </div>
</div>

<style lang="scss" src="./account-form-content.scss"></style>
